import { ApiSettings, LocalStorageKeyConstants, PaginationConstants } from "../constants/constants";
import { ListLayouts } from "../enums/base.enums";

export class PaginationUtilities {

    // Separates a list from its layout inside a stored key ("MS-USER-LIST|table"). Writing a key and
    // matching one both go through buildStorageKey so the two can never drift apart.
    private static readonly STORAGE_KEY_SEPARATOR = '|';

    // A tag name containing a hyphen is a custom element, which is how a component's host element is
    // told apart from the plain markup between it and its pagination.
    private static readonly CUSTOM_ELEMENT_TAG_MARKER = '-';

    private static rowHeightsByList?: Map<string, number>;

    /**
     * A first page sized to fill the viewport instead of a flat 20 rows.
     *
     * Infinite scroll can only carry a list forward once that list is tall enough to scroll, so a
     * fixed page size strands tall and vertical monitors: 20 rows leave the scroll container half
     * empty, with no scrollbar to pull the next page in. Topping the list up afterwards does
     * un-strand it, but the user watches it happen — the list visibly reflows and grows a beat after
     * it first painted. Asking for enough rows up front means the first paint is already scrollable,
     * and the skeleton placeholders (which are built from this same count) already occupy the space
     * the real rows will land in.
     *
     * How tall a row is, is never assumed — it is measured from that list's own rendered rows by
     * reportRowHeight() and remembered. Until a list has been measured this returns the plain default
     * page size, so its first ever render under-fills a tall screen and leans on the sentinel top-up
     * to finish (which advances by that same default, so a very tall screen takes a few rounds). That
     * render is what supplies the measurement, and every load afterwards is sized from it.
     *
     * The whole viewport height is used even though the table is capped shorter than that, so the
     * count comes out with the height of that chrome to spare. Note the API caps a page at 100 rows,
     * so a viewport tall enough to want more than that cannot be filled in one request no matter how
     * good the measurement is; the sentinel top-up in PaginationComponent remains the backstop.
     */
    public static getViewportPageSize(listKey: string): number {
        const measuredRowHeight = PaginationUtilities.getShortestMeasuredRow(listKey);

        if (!measuredRowHeight) return ApiSettings.DEFAULT_TAKE();

        const rowsToFillViewport = Math.ceil(window.innerHeight / measuredRowHeight);

        return Math.min(
            Math.max(rowsToFillViewport, ApiSettings.DEFAULT_TAKE()),
            PaginationConstants.PAGINATION_MAX_PAGE_SIZE()
        );
    }

    /**
     * Records how tall a list's rows really are, from a row the browser has actually laid out.
     *
     * Each list renders two layouts — a table and, below the sm breakpoint, a card stack — whose
     * units of vertical space differ by several times. They are recorded separately under the same
     * list so that measuring one never overwrites the other, and the page size is then taken from
     * whichever is shorter, which is the layout that needs the most rows to fill a screen.
     *
     * Within a layout this replaces rather than narrows: it is the current truth for that layout, so
     * a list that becomes denser or roomier is corrected in either direction on its next render.
     */
    public static reportRowHeight(listKey: string, layout: ListLayouts, rowHeightPixels: number): void {
        // A row with no height has not been laid out (the layout the viewport is not using, a
        // mid-render pass); it says nothing about the CSS and must not be recorded. Nor is there
        // anywhere to file a measurement whose list could not be identified.
        if (rowHeightPixels <= 0 || !listKey) return;

        const rowHeights = PaginationUtilities.getRowHeights();
        const storageKey = PaginationUtilities.buildStorageKey(listKey, layout);

        if (rowHeights.get(storageKey) === rowHeightPixels) return;

        rowHeights.set(storageKey, rowHeightPixels);
        PaginationUtilities.persistRowHeights(rowHeights);
    }

    /**
     * Identifies the list a pagination sits in by its component's own element, so the measurement is
     * stored against the list it was taken from. Walks up to the nearest custom element, which is the
     * host of the component that owns these rows.
     */
    public static getListKey(element: HTMLElement | null): string {
        let current: HTMLElement | null = element;

        while (current) {
            if (current.tagName.includes(PaginationUtilities.CUSTOM_ELEMENT_TAG_MARKER)) return current.tagName;
            current = current.parentElement;
        }

        return '';
    }

    //#region Private Methods

    /**
     * The shortest row recorded for this list across its layouts — the one that needs the most rows
     * to fill a screen, so a page sized from it fills whichever layout the viewport is showing.
     */
    private static getShortestMeasuredRow(listKey: string): number | undefined {
        const listPrefix = PaginationUtilities.buildStorageKey(listKey);
        let shortest: number | undefined;

        PaginationUtilities.getRowHeights().forEach((rowHeight, storageKey) => {
            if (!storageKey.startsWith(listPrefix)) return;
            if (shortest === undefined || rowHeight < shortest) shortest = rowHeight;
        });

        return shortest;
    }

    /** Omitting the layout yields the prefix every one of that list's layouts is stored under. */
    private static buildStorageKey(listKey: string, layout?: ListLayouts): string {
        return `${listKey}${PaginationUtilities.STORAGE_KEY_SEPARATOR}${layout ?? ''}`;
    }

    private static getRowHeights(): Map<string, number> {
        if (PaginationUtilities.rowHeightsByList) return PaginationUtilities.rowHeightsByList;

        PaginationUtilities.rowHeightsByList = new Map<string, number>();

        try {
            const stored = localStorage.getItem(LocalStorageKeyConstants.LIST_ROW_HEIGHTS());
            if (stored) {
                // The annotation describes what we wrote, not what is actually there — this is shared,
                // editable browser storage. Every value is checked, because anything that is not a
                // usable height would divide into NaN and leave every list with an invalid page size.
                Object.entries(JSON.parse(stored) as Record<string, number>)
                    .forEach(([storageKey, rowHeight]) => {
                        if (!Number.isFinite(rowHeight) || rowHeight <= 0) return;
                        PaginationUtilities.rowHeightsByList!.set(storageKey, rowHeight);
                    });
            }
        } catch {
            // Unreadable or malformed storage just means nothing has been measured yet — every list
            // re-measures on its next render, so there is nothing to recover.
        }

        return PaginationUtilities.rowHeightsByList;
    }

    private static persistRowHeights(rowHeights: Map<string, number>): void {
        try {
            localStorage.setItem(
                LocalStorageKeyConstants.LIST_ROW_HEIGHTS(),
                JSON.stringify(Object.fromEntries(rowHeights))
            );
        } catch {
            // Storage being full or blocked only costs the measurement between sessions; the
            // in-memory map still sizes every list correctly for the rest of this one.
        }
    }

    //#endregion
}
